import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { CreateChancel, CustomDelete, MinLoader, SimpleModal, SimpleTooltip, TextRow } from 'components';
import { SignaturePanel } from '../../common/signaturePanel';
import { expandedSignatureEnums, serviceApptDetailList } from './constants';
import { SIGNATURE_ACTION_TYPE } from '../../common/constants';
import { ActualTimes } from './actualTimes';
import { appointmentService } from '../../../../../store/appointment/appointment.service';
import { appointmentActions, authActions, clientActions, httpRequestsOnSuccessActions } from 'store';
import { RENDER_APPOINTMENTS_LIST } from '../../../constants';
import { changeDateTime } from '../constants';
import { signatureTypeEnums } from '../constants';
import { ApptErrors } from '../common/apptErrors';
import { RequireInfoBox } from './requireInfoBox';
import { DownloadFile, FindSuccessItem, previewFile, Images, noteStatusEnums, PermissionList, RolePermission, useModal } from 'utils';
import { ApptSessionNotes } from './apptSessionNotes';
import { apptEnums } from '../../constants';
import { RenderRequirementsModal } from './renderRequirementsModal';



export const CompleteAppt = ({ handleClose, fromSession }) => {
    const { loadDownload, handleDownload, currentId } = DownloadFile();
    const { open, close } = useModal();
    const { item } = useSelector((state) => ({
        item: state.appointment.appointmentById,
    }));
    const dispatch = useDispatch();
    const [openRequirements, setOpenRequirements] = useState(false);
    const [expandedSignature, setExpandedSignature] = useState(null); // expandedSignatureEnums.PROVIDER | expandedSignatureEnums.CLIENT | null
    const success = FindSuccessItem(SIGNATURE_ACTION_TYPE);
    const [inputs, setInputs] = React.useState({
        actualStartTime: item?.actualStartTime ? item?.actualStartTime?.slice(11, 16) : item?.startTime.slice(11, 16),
        actualEndTime: item?.actualEndTime ? item?.actualEndTime?.slice(11, 16) : item?.endTime.slice(11, 16),
        confirm: false,
        loading: false,
    });

    const missingClientSignature = item?.requireSignature && !item?.signature?.key;
    const missingProviderSignature = item?.requireProviderSignature && !item?.providerSignature?.key;
    const missingNote = item?.requireNote && item?.noteCounts?.session?.completed === 0;
    const checkRequired = missingClientSignature || missingProviderSignature || missingNote;
    const checkActualRequired = RolePermission([PermissionList.CONFIRM_APPT_RENDER?.code]);
    const needsTimeConfirmation = checkActualRequired && !inputs?.confirm;
    const tooltipText = needsTimeConfirmation ? 'Please confirm the actual time to complete this appointment.' : '';
    const providerSigned = !!item?.providerSignature?.key;
    const clientSigned = !!item?.signature?.key;

    useEffect(() => {
        dispatch(authActions.getMyProfile());
    }, []);

    useEffect(() => {
        if (item?.client) {
            if (RolePermission([PermissionList.CLIENT_CONTACT_READ?.code])) {
                dispatch(clientActions.getClientsContacts(item?.client?.id));
            } else {
                dispatch(clientActions.getClientsContactInfo(item?.client?.id));
            }
        }
    }, [item]);

    useEffect(() => {
        if (success?.type === SIGNATURE_ACTION_TYPE) {
            dispatch(httpRequestsOnSuccessActions.removeSuccess(SIGNATURE_ACTION_TYPE));
            setExpandedSignature(null);
        }
    }, [success]);

    const handleRemoveSignature = (type) => {
        const signature = type === signatureTypeEnums.PROVIDER_SIGNATURE ? item?.providerSignature : item?.signature;
        const label = type === signatureTypeEnums.PROVIDER_SIGNATURE ? 'Provider' : 'Client';
        open(
            <CustomDelete
                text={`Are you sure you want to delete this signature?`}
                info={`Delete ${label} Signature?`}
                handleDel={() => dispatch(appointmentActions.deleteSignature(item?.id, type))}
                handleClose={() => close()}
                actionType={'DELETE_SIGNATURE'}
                body={
                    <div className="deleting-client-info-box">
                        <img src={Images.signatureRemove} alt={'signature'} />
                        <p>{signature?.originalName}</p>
                    </div>
                }
            />
        );
    };

    const handleComplete = async () => {
        if (needsTimeConfirmation) return;
        if (checkRequired) {
            setOpenRequirements(true);
            return;
        }
        setInputs((prevState) => ({ ...prevState, loading: true, error: '' }));
        try {
            await appointmentService.completeApptService(item?.id, {
                actualStartTime: changeDateTime(item?.startDate, inputs?.actualStartTime),
                actualEndTime: changeDateTime(item?.startDate, inputs?.actualEndTime),
            });
            handleClose();
            dispatch(appointmentActions.getAppointmentById(item?.id));
            dispatch(httpRequestsOnSuccessActions.appendSuccess(RENDER_APPOINTMENTS_LIST));
        } catch (e) {
            setInputs((prevState) => ({ ...prevState, error: e?.data?.message }));
        } finally {
            setInputs((prevState) => ({ ...prevState, loading: false }));
        }
    };

    return (
        <div style={{ width: 1260, maxWidth: '100%' }}>
            <div>
                <p className="appt-detail-title">Complete Appt {item?.displayId}</p>
            </div>

            <div className="space-between-flex-start" style={{ margin: '16px 0 12px', alignItems: 'flex-start' }}>
                <div className="full-width">
                    {serviceApptDetailList(item)?.map(
                        (i, j) =>
                            i?.value && (
                                <div key={j} className="appt-detail-box">
                                    <p className="appt-detail-modal-title">{i?.title}</p>
                                    <p className={i?.link ? 'appt-detail-modal-link-value' : 'appt-detail-modal-value'}>{i?.value}</p>
                                </div>
                            )
                    )}
                </div>
                <RequireInfoBox styles={{ margin: 0 }} />
            </div>

            <div>
                <div className="appt-session-notes-flow" style={{ position: 'relative' }}>
                    <ApptSessionNotes item={item} fromComplete={true} />
                </div>

                <div>
                    <p className="note-table-title" style={{ textAlign: 'start', marginTop: 12 }}>
                        Signatures
                    </p>

                    <div className="complete-appt-signatures-summary">
                        {/* Provider Signature Row */}
                        <div className={`signature-summary-row ${expandedSignature === expandedSignatureEnums.PROVIDER ? 'expanded' : ''}`}>
                            <div className="signature-summary-header">
                                <div className="signature-summary-left">
                                    <img
                                        style={
                                            expandedSignature === expandedSignatureEnums.PROVIDER
                                                ? {
                                                      filter: 'brightness(0) saturate(100%) invert(24%) sepia(92%) saturate(3268%) hue-rotate(209deg) brightness(95%) contrast(91%)',
                                                  }
                                                : {}
                                        }
                                        src={Images.signIcon}
                                        alt="signature"
                                        className="signature-summary-icon"
                                    />
                                    <span className="signature-summary-label">Provider Signature</span>
                                    {providerSigned && expandedSignature !== expandedSignatureEnums.PROVIDER ? (
                                        <>
                                            <div className="signature-info-preview-box">
                                                <img src={item?.providerSignature?.url} alt="signature" className="signature-preview-img" />
                                            </div>

                                            <div className="signature-info-preview-box">
                                                <img
                                                    style={{
                                                        filter: 'brightness(0) saturate(100%) invert(47%) sepia(7%) saturate(1144%) hue-rotate(183deg) brightness(90%) contrast(88%)',
                                                    }}
                                                    src={Images.signatureDate}
                                                    alt="date"
                                                />
                                                <span>{moment(item?.providerSignatureDate).local().format('MM/DD/YYYY, hh:mm A')}</span>
                                            </div>
                                            <div className="signature-info-preview-box">
                                                <img
                                                    style={{
                                                        filter: 'brightness(0) saturate(100%) invert(47%) sepia(7%) saturate(1144%) hue-rotate(183deg) brightness(90%) contrast(88%)',
                                                    }}
                                                    src={Images.signatureLocation}
                                                    alt="location"
                                                />
                                                <TextRow
                                                    name={
                                                        item?.providerSignatureLocation?.formattedAddress
                                                            ? item?.providerSignatureLocation?.formattedAddress
                                                            : 'Not Set'
                                                    }
                                                />
                                            </div>

                                            <div className="signature-action-icons">
                                                <button
                                                    className="square-btn-box"
                                                    onClick={() => previewFile(item?.providerSignature)}>
                                                    <img height={20} src={Images.showEye} alt="view" />
                                                </button>
                                                <button
                                                    className="square-btn-box"
                                                    onClick={() => handleDownload(item?.providerSignature, item?.providerSignature?.key)}>
                                                    {loadDownload && currentId === item?.providerSignature?.key ? (
                                                        <MinLoader small={true} color={'#475467'} margin={'0'} position={'relative'} />
                                                    ) : (
                                                        <img height={20} src={Images.downloadCloud} alt="download" />
                                                    )}
                                                </button>
                                                {item?.status === apptEnums.PENDING && (
                                                    <button
                                                        className="square-btn-box delete"
                                                        onClick={() => handleRemoveSignature(signatureTypeEnums.PROVIDER_SIGNATURE)}>
                                                        <img height={20} src={Images.redTrash} alt="delete" />
                                                    </button>
                                                )}
                                            </div>
                                        </>
                                    ) : (
                                        expandedSignature !== expandedSignatureEnums.PROVIDER && (
                                            <span className="signature-summary-status">Not Signed</span>
                                        )
                                    )}
                                </div>
                                {providerSigned && expandedSignature !== expandedSignatureEnums.PROVIDER ? (
                                    <button
                                        className="signature-summary-resign-btn"
                                        onClick={() => setExpandedSignature(expandedSignatureEnums.PROVIDER)}>
                                        <img src={Images.historyTab} alt="re-sign" />
                                        Re-sign
                                    </button>
                                ) : (
                                    !providerSigned &&
                                    expandedSignature !== expandedSignatureEnums.PROVIDER && (
                                        <button
                                            className="signature-summary-sign-btn"
                                            onClick={() => setExpandedSignature(expandedSignatureEnums.PROVIDER)}>
                                            Sign
                                        </button>
                                    )
                                )}
                            </div>
                            {expandedSignature === expandedSignatureEnums.PROVIDER && (
                                <div style={{ padding: '0 16px 12px 16px' }}>
                                    <SignaturePanel
                                        doNotShowAlreadyUploaded={true}
                                        fromComplete={true}
                                        appt={item}
                                        provider={true}
                                        showSavedSignature
                                        showFooter
                                        onCancel={() => setExpandedSignature(null)}
                                        onApplied={() => setExpandedSignature(null)}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Client/Guardian Signature Row */}
                        <div className={`signature-summary-row ${expandedSignature === expandedSignatureEnums.CLIENT ? 'expanded' : ''}`}>
                            <div className="signature-summary-header">
                                <div className="signature-summary-left">
                                    <img
                                        style={
                                            expandedSignature === expandedSignatureEnums.CLIENT
                                                ? {
                                                      filter: 'brightness(0) saturate(100%) invert(24%) sepia(92%) saturate(3268%) hue-rotate(209deg) brightness(95%) contrast(91%)',
                                                  }
                                                : {}
                                        }
                                        src={Images.signIcon}
                                        alt="signature"
                                        className="signature-summary-icon"
                                    />
                                    <span className="signature-summary-label">Client/Guardian Signature</span>
                                    {clientSigned && expandedSignature !== expandedSignatureEnums.CLIENT ? (
                                        <>
                                            <div className="signature-info-preview-box">
                                                <img src={item?.signature?.url} alt="signature" className="signature-preview-img" />
                                            </div>
                                            <div className="signature-info-preview-box">
                                                <img
                                                    style={{
                                                        filter: 'brightness(0) saturate(100%) invert(47%) sepia(7%) saturate(1144%) hue-rotate(183deg) brightness(90%) contrast(88%)',
                                                    }}
                                                    src={Images.signatureDate}
                                                    alt="date"
                                                />
                                                <span style={{whiteSpace:'nowrap'}}>{moment(item?.signatureDate).local().format('MM/DD/YYYY, hh:mm A')}</span>

                                                {item?.signorFullName && (
                                                    <div className="signature-info-preview-box">
                                                        <TextRow name={`Signed by: ${item.signorFullName}`} />
                                                    </div>
                                                )}
                                            </div>
                                            <div className="signature-info-preview-box">
                                                <img
                                                    style={{
                                                        filter: 'brightness(0) saturate(100%) invert(47%) sepia(7%) saturate(1144%) hue-rotate(183deg) brightness(90%) contrast(88%)',
                                                    }}
                                                    src={Images.signatureLocation}
                                                    alt="location"
                                                />
                                                <TextRow
                                                    name={
                                                        item?.signatureLocation?.formattedAddress
                                                            ? item?.signatureLocation?.formattedAddress
                                                            : 'Not Set'
                                                    }
                                                />
                                            </div>
                                            {/*<GuardianSelector*/}
                                            {/*    setSelectedGuardian={() => {}}*/}
                                            {/*    selectedGuardian={item?.signorId}*/}
                                            {/*/>*/}
                                            <div className="signature-action-icons">
                                                <button
                                                    className="square-btn-box"
                                                    onClick={() => previewFile(item?.signature)}>
                                                    <img height={20} src={Images.showEye} alt="view" />
                                                </button>
                                                <button
                                                    className="square-btn-box"
                                                    onClick={() => handleDownload(item?.signature, item?.signature?.key)}>
                                                    {loadDownload && currentId === item?.signature?.key ? (
                                                        <MinLoader small={true} color={'#475467'} margin={'0'} position={'relative'} />
                                                    ) : (
                                                        <img height={20} src={Images.downloadCloud} alt="download" />
                                                    )}
                                                </button>
                                                {item?.status === apptEnums.PENDING && (
                                                    <button
                                                        className="square-btn-box delete"
                                                        onClick={() => handleRemoveSignature(signatureTypeEnums.SIGNATURE)}>
                                                        <img height={20} src={Images.redTrash} alt="delete" />
                                                    </button>
                                                )}
                                            </div>
                                        </>
                                    ) : (
                                        expandedSignature !== expandedSignatureEnums.CLIENT && (
                                            <span className="signature-summary-status">Not Signed</span>
                                        )
                                    )}
                                </div>
                                {clientSigned && expandedSignature !== expandedSignatureEnums.CLIENT ? (
                                    <button
                                        className="signature-summary-resign-btn"
                                        onClick={() => setExpandedSignature(expandedSignatureEnums.CLIENT)}>
                                        <img src={Images.historyTab} alt="re-sign" />
                                        Re-sign
                                    </button>
                                ) : (
                                    !clientSigned &&
                                    expandedSignature !== expandedSignatureEnums.CLIENT && (
                                        <button
                                            className="signature-summary-sign-btn"
                                            onClick={() => setExpandedSignature(expandedSignatureEnums.CLIENT)}>
                                            Sign
                                        </button>
                                    )
                                )}
                            </div>
                            {expandedSignature === expandedSignatureEnums.CLIENT && (
                                <div style={{ padding: '0 16px 12px 16px' }}>
                                    <SignaturePanel
                                        doNotShowAlreadyUploaded={true}
                                        fromComplete={true}
                                        appt={item}
                                        provider={false}
                                        hideSaveDefault={true}
                                        // showSavedSignature
                                        showGuardianSelector
                                        showFooter
                                        onCancel={() => setExpandedSignature(null)}
                                        onApplied={() => setExpandedSignature(null)}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="space-between-flex-start" style={{ margin: '16px 0 24px 0' }}>
                <div style={{ width: '49%' }}>
                    <div style={{ marginTop: '-16px' }}>
                        <ApptErrors
                            inputs={{
                                ...inputs,
                                startTime: changeDateTime(
                                    item?.startDate,
                                    inputs?.actualStartTime ? inputs?.actualStartTime : item?.startTime
                                ),
                                endTime: changeDateTime(item?.startDate, inputs?.actualEndTime ? inputs?.actualEndTime : item?.endTime),
                            }}
                            currentAuthorization={item?.authorizedService}
                            modalDate={item}
                            backError={{
                                error: inputs?.error,
                            }}
                        />
                    </div>
                </div>
                {checkActualRequired && <ActualTimes inputs={inputs} setInputs={setInputs} />}
            </div>

            {item?.status === apptEnums.PENDING && (
                <div>
                    <SimpleTooltip
                        disableHoverListener={!needsTimeConfirmation}
                        disableTouchListener={!needsTimeConfirmation}
                        title={<p>{tooltipText}</p>}
                        placement="top-end">
                        <div style={{ display: 'inline-block', width: '100%' }}>
                            <CreateChancel
                                disabled={needsTimeConfirmation}
                                loader={inputs?.loading}
                                create={'Complete Appt'}
                                chancel={'Cancel'}
                                onCreate={handleComplete}
                                onClose={handleClose}
                                cancelButtonWidth="auto"
                                buttonWidth="152px"
                                justifyContent={'flex-end'}
                            />
                        </div>
                    </SimpleTooltip>
                </div>
            )}

            <SimpleModal
                handleOpenClose={() => setOpenRequirements(false)}
                openDefault={openRequirements}
                content={
                    <div className="modal-wrapper-style">
                        <div className="modal-body-style">
                            <RenderRequirementsModal handleClose={() => setOpenRequirements(false)} />
                        </div>
                    </div>
                }
            />
        </div>
    );
};
